<template>
    <div>
        <h6 v-if="payload.helperText" style="width: 100%; text-align: center; margin-bottom: 20px">
            {{ payload.helperText }}
        </h6>
        <transition name="slide" mode="out-in">
            <div v-if="!emailHint && !loading" key="1" style="min-height:260px">
                <form @submit.prevent="handleClick">
                    <div v-if="confirmEmailState">
                        <p class="mb-5">
                            {{ $t('components.miscellaneous.verifyModuleDialog.doubleCheckEmail') }}
                        </p>
                        <h5 class="text-center w-100">{{ fullEmailAddress }}</h5>
                        <div class="d-flex justify-content-around">
                            <BaseButton
                                style="display: block; margin: 20px auto; max-width: 300px;"
                                class="outline-primary mt-5"
                                @click="cancelConfirm"
                            >
                                {{ $t('general.cancel') }}
                            </BaseButton>
                            <BaseButton
                                submit
                                style="display: block; margin: 20px auto; max-width: 300px;"
                                class="outline-primary mt-5"
                            >
                                {{ $t('components.miscellaneous.verifyModuleDialog.verify') }}
                            </BaseButton>
                        </div>
                    </div>
                    <div v-else>
                        <h6 class="subtitle bold">
                            {{ $t('components.miscellaneous.verifyModuleDialog.university') }}
                        </h6>

                        <BaseSearchable
                            v-model="school"
                            :data="schoolData()"
                            class="mb-4"
                            required
                        />

                        <div v-if="isConnectedToSelectedSchool">
                            {{
                                $t(
                                    'components.miscellaneous.verifyModuleDialog.alreadyVerifiedInfo'
                                )
                            }}
                        </div>
                        <div v-else>
                            <h6 class="subtitle bold">
                                <template v-if="!noAcademicEmail">
                                    {{
                                        $t(
                                            'components.miscellaneous.verifyModuleDialog.studentEmail'
                                        )
                                    }}
                                </template>
                                <template v-else>
                                    {{ $t('components.miscellaneous.verifyModuleDialog.message') }}
                                </template>
                            </h6>
                            <div
                                class="d-flex flex-row align-items-center custom-outline"
                                style="border: 1px solid rgba(0,0,0,0.4); border-radius: var(--border-radius); padding: .5px .5px;"
                            >
                                <TextField
                                    v-if="!noAcademicEmail"
                                    v-model="email"
                                    required
                                    :padding="false"
                                    style="flex-basis: 0; flex-grow: 1; border: none;"
                                    class="remove-background-from-children"
                                    inputClass="no-right-padding"
                                    :class="{ rightAlignTextInChildren: !noAcademicEmail }"
                                    :outline="false"
                                />

                                <TextArea
                                    v-else
                                    v-model="message"
                                    required
                                    :placeholder="
                                        $t('components.miscellaneous.verifyModuleDialog.message')
                                    "
                                    @ctrlEnter="handleClick()"
                                />

                                <div
                                    :style="{ display: !noAcademicEmail ? 'flex' : 'none' }"
                                    class="align-items-center"
                                >
                                    <p class="m-0 noselect">@</p>
                                    <BaseSearchable
                                        id="asdfSelector"
                                        v-model="emailDomain"
                                        required
                                        :data="uniDomains"
                                        style="flex-basis: 0; flex-grow: 1;"
                                        inputClass="no-left-padding"
                                        :outline="false"
                                    />
                                    <b-tooltip
                                        ref="emailDomainToolTip"
                                        :show.sync="showToolTip"
                                        :disabled="true"
                                        target="asdfSelector"
                                        title="Please choose the right email domain here"
                                    />
                                </div>
                            </div>

                            <div class="mt-1 d-flex flex-row justify-content-end">
                                <span
                                    role="button"
                                    class="no-academic-button"
                                    @click="toggleEmailType"
                                >
                                    {{
                                        noAcademicEmail
                                            ? $t(
                                                  'components.miscellaneous.verifyModuleDialog.haveAcademicEmail'
                                              )
                                            : $t(
                                                  'components.miscellaneous.verifyModuleDialog.doNotHaveAcademicEmail'
                                              )
                                    }}
                                </span>
                            </div>

                            <div v-if="noAcademicEmail" class="mt-2 alert alert-info">
                                {{
                                    $t(
                                        'components.miscellaneous.verifyModuleDialog.noAcademicEmailInfo'
                                    )
                                }}
                            </div>
                            <BaseButton
                                submit
                                style="display: block; margin: 20px auto; width: 100%; max-width: 300px;"
                                class="outline-primary mt-5"
                                :disabled="!buttonEnabled"
                            >
                                {{
                                    noAcademicEmail
                                        ? $t(
                                              'components.miscellaneous.verifyModuleDialog.sendRequest'
                                          )
                                        : $t('components.miscellaneous.verifyModuleDialog.verify')
                                }}
                            </BaseButton>
                        </div>
                    </div>
                </form>
            </div>

            <div v-else-if="loading" key="2">
                <Spinner class="primary-color" style="display: block; margin: 40px auto;" />
            </div>

            <div v-else key="3">
                <h6
                    class="bold px-4"
                    v-html="
                        noAcademicEmail
                            ? $t(
                                  'components.miscellaneous.verifyModuleDialog.verificationRequestSentNonAcademic',
                                  {
                                      schoolName: school.label,
                                  }
                              )
                            : $t('components.miscellaneous.verifyModuleDialog.emailHasBeenSentTo', {
                                  email: fullEmailAddress,
                                  schoolName: school.label,
                              })
                    "
                />

                <div class="py-2 px-4 mt-5">
                    <h6 v-if="!noAcademicEmail" class="mute text-center caption">
                        {{ $t('components.miscellaneous.verifyModuleDialog.ifNoEmailRecieved') }}
                    </h6>

                    <BaseButton
                        class="my-1"
                        style="display: block; margin: 8px auto;"
                        tiny
                        @click="contactHelp"
                    >
                        {{ $t('components.miscellaneous.verifyModuleDialog.contactHelp') }}
                    </BaseButton>
                </div>

                <BaseButton
                    style="display: block; margin: 20px auto; width: 100%; max-width: 300px;"
                    class="outline-primary mt-1"
                    @click="closeDialog"
                >
                    {{ $t('general.ok') }}
                </BaseButton>
            </div>
        </transition>
    </div>
</template>

<script>
// Components
import Spinner from '~/components/builders/Spinner';

// Utils
import { emailVerificationURL } from '~/utils/urlUtils';
import { uniConnectionErrorMapper } from '~/utils/errors/uniErrorMapper';
import DialogService from '~/services/DialogService';
import IntercomService from '~/services/IntercomService';
import SchoolService from '~/services/SchoolService';

import { extractErrorResponse } from '~/utils/errors/authErrorMapper';

import BackgroundProcessesService from '~/services/BackgroundProcessesService';

import emailValidator from 'email-validator';

export default {
    name: 'VerifyModuleDialog',
    components: { Spinner },
    data() {
        return {
            school: undefined,
            emailHint: false,
            queryLoading: false,
            email: '',
            emailDomain: undefined,
            message: '',
            shouldForceUpdate: true,
            loadingConnections: true,
            showToolTip: false,
            noAcademicEmail: false,
            confirmEmailState: false,
        };
    },
    computed: {
        schools() {
            return this.$interfaces.university.get({ reactive: true });
        },

        loading() {
            return this.schools.loading || this.queryLoading || this.loadingConnections;
        },

        connections() {
            let forceUpdate = this.shouldForceUpdate;
            return this.$interfaces.university.connection.get({
                forceUpdate,
                reactive: true,
            });
        },

        connectionsLoading() {
            return this.connections.loading;
        },

        payload() {
            return this.$interfaces.dialogModal.getState().payload || {};
        },

        currentSchool() {
            if (!this.schools.loading) {
                const currentSubDomain = SchoolService.getCurrentSchoolDomain();
                let currentSchool = this.schools.data.find(it => it.subDomain === currentSubDomain);

                if (!currentSchool && this.schools.data.length > 0) {
                    currentSchool = this.schools.data[0];
                } else if (!currentSchool) {
                    return {};
                }

                return { value: currentSchool.name, label: currentSchool.name };
            }
            return null;
        },

        selectedSchool() {
            return (this.schools && this.schools.data && this.schools.data.length > 0
                ? this.schools.data
                : []
            ).find(it => it.name === ((this.school || {}).label || ''));
        },

        isConnectedToSelectedSchool() {
            if (this.selectedSchool && !this.connectionsLoading && this.connections.data) {
                const foundConnection = this.connections.data.find(
                    c => c.schoolId === this.selectedSchool.id
                );

                return foundConnection !== undefined;
            }

            return false;
        },

        emailValid() {
            return emailValidator.validate(this.fullEmailAddress);
        },

        buttonEnabled() {
            if (this.noAcademicEmail) {
                return true;
            } else {
                return this.emailValid;
            }
        },

        fullEmailAddress() {
            if (this.noAcademicEmail) {
                if (!this.user) return null;

                return this.user.email;
            }

            if (!!this.email && !!this.emailDomain)
                return this.email.split('@')[0].concat('@', this.emailDomain.value);

            return null;
        },

        user() {
            const user = this.$interfaces.user.me.get({ reactive: true });
            if (!user) return null;
            return user.data;
        },
        uniDomains() {
            if (this.school && this.selectedSchool) {
                const school = this.selectedSchool;
                const domains = school.domains.map(it => ({
                    value: it,
                    label: it,
                }));
                return domains;
            }
            return [];
        },
    },
    methods: {
        cancelConfirm() {
            this.confirmEmailState = false;
        },
        schoolData() {
            return this.schools.data.map(s => ({ value: s.id, label: s.name }));
        },
        loadingSchools() {
            return this.schools.loading;
        },
        async handleClick() {
            if (!this.noAcademicEmail && !this.confirmEmailState) {
                this.confirmEmailState = true;
                return;
            }
            this.queryLoading = true;

            const school = this.selectedSchool;

            this.$interfaces.university.connection
                .post({
                    body: {
                        noAcademicEmail: this.noAcademicEmail,
                        schoolId: school.id,
                        studentEmail: this.fullEmailAddress,
                        message: this.noAcademicEmail ? this.message : null,
                        returnUrl: emailVerificationURL(),
                    },
                })
                .then(() => {
                    DialogService.setTitle(
                        this.$t('components.miscellaneous.verifyModule.verificationPending')
                    );
                    this.emailHint = true;
                    this.queryLoading = false;
                    // Start ping loop to update verification progress
                    BackgroundProcessesService.startVerificationPing();
                })
                .catch(error => {
                    let errors = extractErrorResponse(error) || [];
                    const err = errors.length > 0 ? errors[0] : {};

                    if (err.code === 'ExistingConnection') {
                        this.$interfaces.university.connection
                            .get({ forceUpdate: true })
                            .then(data => {
                                //Check if has pending, and if so open avatar dialog
                                let connection = data.data.find(it => !it.isVerified);
                                if (data.data.length > 0 && connection) {
                                    this.$interfaces.dialogModal.getState().resolver.res({
                                        isPending: true,
                                        connection: connection,
                                    });
                                } else {
                                    this.$interfaces.dialogModal
                                        .getState()
                                        .resolver.res({ isPending: false });
                                }
                                //Else accept promise with enum, so jobdetail can continue apply click
                            })
                            .catch(() => {});
                    } else {
                        this.$interfaces.dialogModal.openModal({
                            title: 'Error',
                            content: uniConnectionErrorMapper(error),
                            actions: [
                                {
                                    title: this.$t('general.ok'),
                                    variant: 'primary',
                                    action: ({ res }) => res(),
                                },
                            ],
                        });
                    }
                });
        },
        closeDialog() {
            this.$interfaces.dialogModal.getState().resolver.res();
        },

        toggleEmailType() {
            this.noAcademicEmail = !this.noAcademicEmail;
        },

        checkDomainInEmail(noAcademicEmail, newEmail, oldEmail = '') {
            if (!noAcademicEmail && (newEmail.includes('@') || oldEmail.includes('@'))) {
                this.$refs.emailDomainToolTip.$emit('enable');
                this.showToolTip = true;
            } else {
                this.$refs.emailDomainToolTip.$emit('disable');
                this.showToolTip = false;
            }
        },
        contactHelp() {
            IntercomService.show();
        },
    },

    watch: {
        currentSchool(newSchool) {
            if (this.school === undefined) this.school = newSchool;
        },
        connectionsLoading(isLoading, wasLoading) {
            if (!isLoading && wasLoading && this.loadingConnections) {
                this.loadingConnections = false;
                if (
                    !this.connections.loading &&
                    !this.schools.loading &&
                    this.payload.checkConnections
                ) {
                    let connection = this.connections.data.find(
                        it => it.school.name === this.currentSchool.value
                    );
                    if (connection && connection.isVerified) {
                        this.$interfaces.dialogModal.getState().resolver.res({ isVerified: true });
                    } else if (connection) {
                        this.$interfaces.dialogModal.getState().resolver.res();
                        this.$interfaces.uniBadgeModule.setOpenAndSelected({
                            isOpen: true,
                            selected: connection,
                        });
                    }
                }
            }
        },
        noAcademicEmail(current) {
            this.checkDomainInEmail(current, this.email, this.email);
        },
        email(newEmail, oldEmail) {
            this.checkDomainInEmail(this.noAcademicEmail, newEmail, oldEmail);
        },
        uniDomains(domains) {
            if (!!domains.length) this.emailDomain = domains[0];
        },
        connections(conn) {
            if (!!conn) this.shouldForceUpdate = false;
        },
    },

    mounted() {
        if (this.schools && this.schools.data && this.schools.data.length > 0) {
            this.school = this.currentSchool;
        }
    },
};
</script>

<style lang="scss">
.custom-outline:focus-within {
    border-color: var(--primary) !important;
}
.remove-background-from-children input {
    background-color: transparent !important;
}
.remove-background-from-children > div > input:focus {
    outline-color: transparent !important;
}
.rightAlignTextInChildren input {
    text-align: right;
}
.no-academic-button {
    font-size: 0.8em;
    color: var(--primary);
    cursor: pointer;

    &:hover {
        text-decoration: underline;
    }
}
.no-right-padding {
    padding-right: 0px !important;
}

.no-left-padding {
    padding-left: 0px !important;
}
</style>
